const CACHE_KEY = 'sugarhalo.deliveryLocation.v1';
const CACHE_DURATION = 24 * 60 * 60 * 1000;

const US_STATES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeUsState(value) {
  const cleaned = clean(value);
  if (!cleaned) return '';

  const code = cleaned.includes('-') ? cleaned.split('-').pop() : cleaned;
  const upperCode = code.toUpperCase();

  if (US_STATES[upperCode]) return US_STATES[upperCode];

  return Object.values(US_STATES).find((state) => state.toLowerCase() === cleaned.toLowerCase()) || cleaned;
}

function addBusinessDays(sourceDate, days) {
  const date = new Date(sourceDate.getTime());
  date.setHours(12, 0, 0, 0);

  let addedDays = 0;
  while (addedDays < days) {
    date.setDate(date.getDate() + 1);

    const day = date.getDay();
    if (day !== 0 && day !== 6) {
      addedDays += 1;
    }
  }

  return date;
}

function sameDate(firstDate, secondDate) {
  return firstDate.getFullYear() === secondDate.getFullYear()
    && firstDate.getMonth() === secondDate.getMonth()
    && firstDate.getDate() === secondDate.getDate();
}

function formatDate(date) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function formatDateRange(startDate, endDate) {
  if (sameDate(startDate, endDate)) {
    return formatDate(startDate);
  }

  return `${formatDate(startDate)}-${formatDate(endDate)}`;
}

function parseWholeNumber(value, fallback) {
  const parsedValue = parseInt(value, 10);

  return Number.isNaN(parsedValue) ? fallback : parsedValue;
}

function readLocationCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');

    if (!cached || !cached.display || !cached.savedAt) return null;
    if (Date.now() - cached.savedAt > CACHE_DURATION) return null;

    return cached.display;
  } catch (error) {
    return null;
  }
}

function writeLocationCache(display) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      display,
      savedAt: Date.now(),
    }));
  } catch (error) {
    // Storage can be unavailable in private browsing or strict privacy modes.
  }
}

function parseLocation(data) {
  if (!data || data.error || data.success === false) return null;

  const postal = clean(
    data.postal
      || data.postal_code
      || data.postalCode
      || data.zip
      || data.zipCode
      || data.zip_code
      || data.postcode
  );

  const regionValue = clean(
    data.region
      || data.regionName
      || data.region_name
      || data.state
      || data.principalSubdivision
      || data.subdivision
  );

  const regionCode = clean(
    data.region_code
      || data.regionCode
      || data.state_code
      || data.stateCode
      || data.principalSubdivisionCode
  );

  const region = normalizeUsState(regionValue) || normalizeUsState(regionCode);
  const displayParts = [postal, region].filter(Boolean);

  return displayParts.length > 0 ? displayParts.join(', ') : null;
}

async function fetchLocation(endpoint) {
  if (!endpoint) return null;

  const response = await fetch(endpoint, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'omit',
  });

  if (!response.ok) return null;

  return parseLocation(await response.json());
}

class SugarHaloDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.locationTarget = this.querySelector('[data-delivery-location]');
    this.estimateTarget = this.querySelector('[data-delivery-estimate]');
    this.fallbackLocation = clean(this.dataset.fallbackLocation) || 'your area';

    this.renderEstimate();
    this.renderCachedLocation();
    this.resolveLocation();
  }

  renderEstimate() {
    if (!this.estimateTarget) return;

    const businessDays = Math.max(parseWholeNumber(this.dataset.businessDays, 5), 0);
    const windowBusinessDays = Math.max(parseWholeNumber(this.dataset.windowBusinessDays, 1), 0);
    const startDate = addBusinessDays(new Date(), businessDays);
    const endDate = addBusinessDays(new Date(), businessDays + windowBusinessDays);

    this.estimateTarget.textContent = formatDateRange(startDate, endDate);
  }

  renderCachedLocation() {
    const cachedLocation = readLocationCache();

    this.renderLocation(cachedLocation || this.fallbackLocation);
  }

  async resolveLocation() {
    try {
      const display = await fetchLocation(this.dataset.locationEndpoint);

      if (display) {
        writeLocationCache(display);
        this.renderLocation(display);
      }
    } catch (error) {
      this.renderLocation(readLocationCache() || this.fallbackLocation);
    }
  }

  renderLocation(display) {
    if (!this.locationTarget) return;

    this.locationTarget.textContent = display || this.fallbackLocation;
  }
}

if (!customElements.get('sugarhalo-delivery-estimate')) {
  customElements.define('sugarhalo-delivery-estimate', SugarHaloDeliveryEstimate);
}
